import React, { useState, useEffect } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { toastActions } from '../../../../components/uielements/toast/toast';
import { IntlMessages } from '../../../../helper/langHelper';
import AdminModal from '../../../../components/uielements/modal/adminModal';

import {
  FormWrapper,
  FormSection,
  FieldGroup,
  Label,
  ErrorText,
  ButtonContainer,
  PrimaryButton,
} from '../../users/modals/UserPromoteModal.styles';
import {
  UploadBox,
  HiddenFileInput,
  UploadText,
  UploadedFileCard,
  FileInfoRow,
  FileName,
  FileActions,
  ActionButton,
  HelperText,
  UploadingText,
  LinksSection,
  LinkItem,
  LinkInputLabel,
  LinkInput,
  DeleteButton,
  AddMoreButton,
} from '../Clusters.styles';
import { createCluster, updateCluster } from '../../../../services/admin/clusters';
import { saveFile, deleteFile, viewFile } from '../../../../services/admin/files';
import { getPrecinctOfAddress, getZoneList } from '../../../../services/procedureService';
import { UploadIcon } from '../../../../icons';
import { Trash2 } from 'lucide-react';
import styled from 'styled-components';

const QUESTION_TYPE_OPTIONS = [
  { label: 'Text', value: 'TEXT' },
  { label: 'Option', value: 'OPTION' },
  { label: 'Number', value: 'NUMBER' },
  { label: 'Impact', value: 'IMPACT' },
];

const ALLOWED_FILE_TYPES = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const CreateAndUpdateClusterModal = ({
  onClose,
  isEdit = false,
  clusterId = null,
  initialData = null,
}) => {
  const queryClient = useQueryClient();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [question, setQuestion] = useState('');
  const [title, setTitle] = useState('');
  const [questionType, setQuestionType] = useState('TEXT');
  const [validationErrors, setValidationErrors] = useState({});
  const [apiError, setApiError] = useState('');

  // Region / Precinct state (create mode only)
  const [regionId, setRegionId] = useState('');
  const [precinctId, setPrecinctId] = useState('');
  const [regionsData, setRegionsData] = useState([]);
  const [precinctsList, setPrecinctsList] = useState([]);

  // File upload state — supports multiple files
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [fileError, setFileError] = useState('');

  // Links state — supports multiple links
  const [linkArray, setLinkArray] = useState([]);

  // React Query — Fetch Regions/Zones (create mode)
  const { data: zonesQueryData, isLoading: zonesLoading } = useQuery({
    queryKey: ['zones'],
    queryFn: () => getZoneList(),
    enabled: !isEdit,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // React Query — Fetch Precincts based on selected region (create mode)
  const { data: precinctQueryData, isLoading: precinctLoading } = useQuery({
    queryKey: ['precincts', regionId],
    queryFn: () => getPrecinctOfAddress({ regionId }),
    enabled: !isEdit && !!regionId,
  });

  useEffect(() => {
    if (zonesQueryData) setRegionsData(zonesQueryData);
  }, [zonesQueryData]);

  useEffect(() => {
    if (precinctQueryData) {
      setPrecinctsList(precinctQueryData);
    } else {
      setPrecinctsList([]);
      setPrecinctId('');
    }
  }, [precinctQueryData]);

  // Initialize with edit data
  useEffect(() => {
    if (isEdit && initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setQuestion(initialData.question || '');
      setTitle(initialData.title || '');
      setQuestionType(initialData.questionType || 'TEXT');
      setLinkArray(initialData.linkArray || []);
    }
  }, [isEdit, initialData]);

  // File upload mutation
  const fileUploadMutation = useMutation({
    mutationFn: (payload) => saveFile(payload),
    onSuccess: (response, payload) => {
      const newFile = {
        documentId: response?.data?.documentId || response?.documentId,
        fileName: response?.data?.fileName || response?.fileName || payload?.fileName,
        contentType: response?.data?.contentType || response?.contentType,
      };
      setUploadedFiles((prev) => [...prev, newFile]);
      setFileError('');
      toastActions.success(
        <IntlMessages id="toast.file.upload.success" defaultMessage="File uploaded successfully." />
      );
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to upload file. Please try again.';
      setFileError(errorMessage);
      toastActions.error(
        <IntlMessages id="toast.file.upload.error" defaultMessage="Failed to upload file." />
      );
    },
  });

  // File delete mutation
  const fileDeleteMutation = useMutation({
    mutationFn: (payload) => deleteFile(payload),
    onSuccess: (_, variables) => {
      setUploadedFiles((prev) => prev.filter((f) => f.documentId !== variables.documentId));
      setFileError('');
      toastActions.success(
        <IntlMessages id="toast.file.delete.success" defaultMessage="File deleted successfully." />
      );
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete file. Please try again.';
      setFileError(errorMessage);
      toastActions.error(
        <IntlMessages id="toast.file.delete.error" defaultMessage="Failed to delete file." />
      );
    },
  });

  // File view mutation
  const fileViewMutation = useMutation({
    mutationFn: (payload) => viewFile(payload),
    onSuccess: (response) => {
      if (response) {
        const url = response?.documentUrl;
        window.open(url, '_blank');
      }
    },
    onError: () => {
      toastActions.error(
        <IntlMessages id="toast.file.view.error" defaultMessage="Failed to view file." />
      );
    },
  });

  // Create Mutation
  const createMutation = useMutation({
    mutationFn: (payload) => createCluster(payload),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages
          id="toast.cluster.add.success"
          defaultMessage="Cluster created successfully."
        />
      );
      queryClient.invalidateQueries({ queryKey: ['adminClusters'] });
      handleClose();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create cluster. Please try again.';
      toastActions.error(
        <IntlMessages id="toast.cluster.add.error" defaultMessage="Failed to create cluster." />
      );
      setApiError(errorMessage);
    },
  });

  // Update Mutation
  const updateMutation = useMutation({
    mutationFn: (payload) => updateCluster(payload),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages
          id="toast.cluster.update.success"
          defaultMessage="Cluster updated successfully."
        />
      );
      queryClient.invalidateQueries({ queryKey: ['adminClusters'] });
      handleClose();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update cluster. Please try again.';
      toastActions.error(
        <IntlMessages id="toast.cluster.update.error" defaultMessage="Failed to update cluster." />
      );
      setApiError(errorMessage);
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const isFileOperationLoading =
    fileUploadMutation.isPending || fileDeleteMutation.isPending || fileViewMutation.isPending;

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!name.trim()) errors.name = 'Cluster name is required.';
    if (!description.trim()) errors.description = 'Description is required.';

    if (!isEdit) {
      if (!question.trim()) errors.question = 'Question is required.';
      if (!title.trim()) errors.title = 'Title is required.';
      if (!questionType) errors.questionType = 'Question type is required.';
      if (!regionId) errors.regionId = 'Region is required.';
      if (!precinctId) errors.precinctId = 'Precinct is required.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError('');

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setFileError('Only JPG, JPEG, PNG, and WebP files are allowed.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size must not exceed 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target?.result?.split(',')[1];
      const contentType = file.type;

      if (base64String) {
        setIsUploadingFile(true);
        fileUploadMutation.mutate(
          { file: base64String, contentType, fileName: file.name },
          {
            onSettled: () => {
              setIsUploadingFile(false);
              e.target.value = '';
            },
          }
        );
      }
    };

    reader.onerror = () => setFileError('Failed to read file. Please try again.');
    reader.readAsDataURL(file);
  };

  // Handle file delete
  const handleFileDelete = (documentId) => {
    fileDeleteMutation.mutate({ documentId });
  };

  // Handle file view
  const handleFileView = (documentId) => {
    fileViewMutation.mutate({ documentId });
  };

  // Handle link operations
  const handleAddLink = () => {
    setLinkArray((prev) => [...prev, { title: '', link: '' }]);
  };

  const handleLinkChange = (index, field, value) => {
    setLinkArray((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleDeleteLink = (index) => {
    setLinkArray((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) return;

    if (isEdit) {
      const payload = {
        clusterId,
        name: name.trim(),
        description: description.trim(),
        ...(linkArray.length > 0 && { linkArray }),
      };
      updateMutation.mutate(payload);
    } else {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        question: question.trim(),
        title: title.trim(),
        questionType,
        regionId,
        precinctId,
        ...(uploadedFiles.length > 0 && {
          documentArray: uploadedFiles.map((f) => ({ documentId: f.documentId })),
        }),
        ...(linkArray.length > 0 && { linkArray }),
      };
      createMutation.mutate(payload);
    }
  };

  // Handle Close
  const handleClose = () => {
    setName('');
    setDescription('');
    setQuestion('');
    setTitle('');
    setQuestionType('TEXT');
    setRegionId('');
    setPrecinctId('');
    setValidationErrors({});
    setApiError('');
    setUploadedFiles([]);
    setFileError('');
    setLinkArray([]);
    onClose();
  };

  // Field change helpers
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    if (validationErrors.name && value.trim())
      setValidationErrors((prev) => ({ ...prev, name: '' }));
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
    if (validationErrors.description && value.trim())
      setValidationErrors((prev) => ({ ...prev, description: '' }));
  };

  const handleQuestionChange = (e) => {
    const value = e.target.value;
    setQuestion(value);
    if (validationErrors.question && value.trim())
      setValidationErrors((prev) => ({ ...prev, question: '' }));
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    if (validationErrors.title && value.trim())
      setValidationErrors((prev) => ({ ...prev, title: '' }));
  };

  const handleQuestionTypeChange = (e) => {
    setQuestionType(e.target.value);
    if (validationErrors.questionType)
      setValidationErrors((prev) => ({ ...prev, questionType: '' }));
  };

  const handleRegionChange = (e) => {
    const value = e.target.value;
    setRegionId(value);
    setPrecinctId('');
    if (validationErrors.regionId && value)
      setValidationErrors((prev) => ({ ...prev, regionId: '' }));
  };

  const handlePrecinctChange = (e) => {
    const value = e.target.value;
    setPrecinctId(value);
    if (validationErrors.precinctId && value)
      setValidationErrors((prev) => ({ ...prev, precinctId: '' }));
  };

  // Shared input style helper
  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '8px 12px',
    border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
    backgroundColor: isLoading ? '#f5f5f5' : '#fff',
    cursor: isLoading ? 'not-allowed' : 'text',
  });

  const selectStyle = (hasError, extraDisabled = false) => ({
    width: '100%',
    padding: '8px 12px',
    border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
    backgroundColor: isLoading || extraDisabled ? '#f5f5f5' : '#fff',
    cursor: isLoading || extraDisabled ? 'not-allowed' : 'pointer',
  });

  // Footer
  const footer = (
    <ButtonContainer>
      <PrimaryButton
        type="submit"
        form="cluster-form"
        disabled={
          isLoading ||
          isFileOperationLoading ||
          !name.trim() ||
          !description.trim() ||
          (!isEdit &&
            (!question.trim() || !title.trim() || !questionType || !regionId || !precinctId)) ||
          Object.keys(validationErrors).length > 0
        }
      >
        {isLoading
          ? isEdit
            ? 'Updating…'
            : 'Creating…'
          : isEdit
            ? 'Update Cluster'
            : 'Create Cluster'}
      </PrimaryButton>
    </ButtonContainer>
  );

  return (
    <AdminModal
      isOpen={true}
      onClose={handleClose}
      title={isEdit ? 'Edit Cluster' : 'Create Cluster'}
      footer={footer}
      width="580px"
    >
      <FormWrapper id="cluster-form" onSubmit={handleSubmit}>
        {/* API Error */}
        {apiError && (
          <div
            style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px',
              border: '1px solid #ef5350',
            }}
          >
            {apiError}
          </div>
        )}

        {/* Cluster Name */}
        <FormSection>
          <FieldGroup>
            <Label htmlFor="cluster-name">Cluster Name *</Label>
            <input
              id="cluster-name"
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="Enter cluster name..."
              disabled={isLoading}
              style={inputStyle(validationErrors.name)}
            />
            {validationErrors.name && <ErrorText>{validationErrors.name}</ErrorText>}
          </FieldGroup>
        </FormSection>

        {/* Description */}
        <FormSection>
          <FieldGroup>
            <Label htmlFor="description">Description *</Label>
            <textarea
              id="description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter cluster description..."
              disabled={isLoading}
              style={{
                ...inputStyle(validationErrors.description),
                fontFamily: 'inherit',
                minHeight: '80px',
                resize: 'vertical',
              }}
            />
            {validationErrors.description && <ErrorText>{validationErrors.description}</ErrorText>}
          </FieldGroup>
        </FormSection>

        {/* Create-mode-only fields */}
        {!isEdit && (
          <>
            {/* Question */}
            <FormSection>
              <FieldGroup>
                <Label htmlFor="question">Question *</Label>
                <textarea
                  id="question"
                  value={question}
                  onChange={handleQuestionChange}
                  placeholder="Enter question..."
                  disabled={isLoading}
                  style={{
                    ...inputStyle(validationErrors.question),
                    fontFamily: 'inherit',
                    minHeight: '80px',
                    resize: 'vertical',
                  }}
                />
                {validationErrors.question && <ErrorText>{validationErrors.question}</ErrorText>}
              </FieldGroup>
            </FormSection>
            {/* Title */}
            <FormSection>
              <FieldGroup>
                <Label htmlFor="title">Title *</Label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Enter title..."
                  disabled={isLoading}
                  style={inputStyle(validationErrors.title)}
                />
                {validationErrors.title && <ErrorText>{validationErrors.title}</ErrorText>}
              </FieldGroup>
            </FormSection>
            {/* Question Type */}
            <FormSection>
              <FieldGroup>
                <Label htmlFor="question-type">Question Type *</Label>
                <select
                  id="question-type"
                  value={questionType}
                  onChange={handleQuestionTypeChange}
                  disabled={isLoading}
                  style={selectStyle(validationErrors.questionType)}
                >
                  {QUESTION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {validationErrors.questionType && (
                  <ErrorText>{validationErrors.questionType}</ErrorText>
                )}
              </FieldGroup>
            </FormSection>
            {/* Region */}
            <FormSection>
              <FieldGroup>
                <Label htmlFor="region">Select Region *</Label>
                <select
                  id="region"
                  value={regionId}
                  onChange={handleRegionChange}
                  disabled={isLoading || zonesLoading}
                  style={selectStyle(validationErrors.regionId, zonesLoading)}
                >
                  <option value="">-- Select Region --</option>
                  {regionsData.map((region) => (
                    <option key={region._id} value={region._id}>
                      {region?.regionName}
                    </option>
                  ))}
                </select>
                {validationErrors.regionId && <ErrorText>{validationErrors.regionId}</ErrorText>}
              </FieldGroup>
            </FormSection>
            {/* Precinct */}
            <FormSection>
              <FieldGroup>
                <Label htmlFor="precinct">Select Precinct *</Label>
                <select
                  id="precinct"
                  value={precinctId}
                  onChange={handlePrecinctChange}
                  disabled={isLoading || precinctLoading || !regionId}
                  style={selectStyle(validationErrors.precinctId, precinctLoading || !regionId)}
                >
                  <option value="">-- Select Precinct --</option>
                  {precinctsList.map((precinct) => (
                    <option key={precinct._id} value={precinct._id}>
                      {precinct.precinctName}
                    </option>
                  ))}
                </select>
                {validationErrors.precinctId && (
                  <ErrorText>{validationErrors.precinctId}</ErrorText>
                )}
                {!regionId && (
                  <ErrorText style={{ color: '#666', fontSize: '12px' }}>
                    Please select a region first
                  </ErrorText>
                )}
              </FieldGroup>
            </FormSection>
            {/* File Upload — multiple files */}
            <FormSection>
              <FieldGroup>
                <Label>Upload Files (Optional)</Label>

                {fileError && (
                  <div
                    style={{
                      backgroundColor: '#ffebee',
                      color: '#c62828',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      marginBottom: '12px',
                      fontSize: '13px',
                      border: '1px solid #ef5350',
                    }}
                  >
                    {fileError}
                  </div>
                )}

                {/* Uploaded files list */}
                {uploadedFiles.map((file) => (
                  <UploadedFileCard key={file.documentId}>
                    <FileInfoRow>
                      <FileName>✅ {file.fileName}</FileName>
                      <FileActions>
                        <ActionButton
                          type="button"
                          onClick={() => handleFileView(file.documentId)}
                          disabled={isFileOperationLoading || isLoading}
                        >
                          View
                        </ActionButton>
                        <ActionButton
                          type="button"
                          variant="delete"
                          onClick={() => handleFileDelete(file.documentId)}
                          disabled={isFileOperationLoading || isLoading}
                        >
                          Delete
                        </ActionButton>
                      </FileActions>
                    </FileInfoRow>
                  </UploadedFileCard>
                ))}

                {/* Upload box — always visible so more files can be added */}
                <UploadBox
                  htmlFor="file-upload"
                  style={{ marginTop: uploadedFiles.length ? '8px' : 0 }}
                >
                  <UploadIcon style={{ width: '30px', height: '30px' }} />
                  <UploadText>
                    {uploadedFiles.length > 0 ? 'Upload another file' : 'Upload files'}
                  </UploadText>
                  <HiddenFileInput
                    id="file-upload"
                    type="file"
                    accept="image/jpg,image/jpeg,image/png,image/webp"
                    onChange={handleFileUpload}
                    disabled={isLoading || isFileOperationLoading || isUploadingFile}
                  />
                </UploadBox>

                {isUploadingFile && <UploadingText>Uploading file...</UploadingText>}

                <HelperText>
                  Allowed formats: JPG, JPEG, PNG, WebP (Max 5MB each)
                  {uploadedFiles.length > 0 && ` · ${uploadedFiles.length} file(s) added`}
                </HelperText>
              </FieldGroup>
            </FormSection>{' '}
            <FormSection>
              <FieldGroup>
                <Label>Add Links (Optional)</Label>
                <LinksSection>
                  {linkArray.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      {linkArray.map((item, index) => (
                        <LinkItem key={index}>
                          <div>
                            <LinkInputLabel>Link Title</LinkInputLabel>
                            <LinkInput
                              type="text"
                              value={item.title}
                              onChange={(e) => handleLinkChange(index, 'title', e.target.value)}
                              placeholder="e.g., Documentation"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <LinkInputLabel>URL</LinkInputLabel>
                            <LinkInput
                              type="url"
                              value={item.link}
                              onChange={(e) => handleLinkChange(index, 'link', e.target.value)}
                              placeholder="e.g., https://example.com"
                              disabled={isLoading}
                            />
                          </div>
                          <DeleteButton
                            type="button"
                            onClick={() => handleDeleteLink(index)}
                            disabled={isLoading}
                          >
                            <Trash2 size={14} />
                          </DeleteButton>
                        </LinkItem>
                      ))}
                    </div>
                  )}
                  <AddMoreButton type="button" onClick={handleAddLink} disabled={isLoading}>
                    + Add {linkArray.length > 0 ? 'More' : ''} Link
                  </AddMoreButton>
                  <HelperText>
                    {linkArray.length > 0 && `${linkArray.length} link(s) added`}
                  </HelperText>
                </LinksSection>
              </FieldGroup>
            </FormSection>
          </>
        )}
      </FormWrapper>
    </AdminModal>
  );
};

export default CreateAndUpdateClusterModal;
